import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Shield, Sparkles, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import SuggestionContent from "@/components/suggestion-content";
import type { Suggestion } from "@shared/schema";

interface SuggestionWithUser extends Suggestion {
  user?: {
    firstName?: string;
    lastName?: string;
  };
}

type StatusFilter = "all" | "unread" | "completed" | "deferred";
type SortOption = "newest" | "oldest" | "status";

interface SimilarSuggestion {
  id: string;
  content: string;
  similarity: number;
}

export default function AdminSuggestionsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  const [similarDialogOpen, setSimilarDialogOpen] = useState(false);
  const [selectedSuggestionId, setSelectedSuggestionId] = useState<string | null>(null);
  const [similarResults, setSimilarResults] = useState<SimilarSuggestion[]>([]);

  const { data: suggestions = [], isLoading } = useQuery<SuggestionWithUser[]>({
    queryKey: ["/api/admin/suggestions"],
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      return apiRequest("PATCH", `/api/admin/suggestions/${id}/status`, { status });
    },
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ["/api/admin/suggestions"] });
      
      const previousSuggestions = queryClient.getQueryData<SuggestionWithUser[]>(["/api/admin/suggestions"]);
      
      queryClient.setQueryData<SuggestionWithUser[]>(["/api/admin/suggestions"], (old) =>
        old?.map((suggestion) =>
          suggestion.id === id ? { ...suggestion, status } : suggestion
        )
      );
      
      return { previousSuggestions };
    },
    onError: (err, variables, context) => {
      if (context?.previousSuggestions) {
        queryClient.setQueryData(["/api/admin/suggestions"], context.previousSuggestions);
      }
    },
    onSuccess: () => {
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/suggestions"] });
    },
  });

  const findSimilarMutation = useMutation({
    mutationFn: async (suggestionId: string) => {
      const response = await apiRequest("POST", "/api/admin/suggestions/find-similar", { suggestionId }) as unknown;
      return response as { similarSuggestions: SimilarSuggestion[] };
    },
    onSuccess: (data) => {
      setSimilarResults(data.similarSuggestions);
      setSimilarDialogOpen(true);
      if (data.similarSuggestions.length === 0) {
      }
    },
    onError: () => {
    },
  });

  const autoGroupMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/admin/suggestions/auto-group", {}) as unknown;
      return response as { message: string; groupsCreated: number; suggestionsGrouped: number };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/suggestions"] });
    },
    onError: () => {
    },
  });

  const handleFindSimilar = (suggestionId: string) => {
    setSelectedSuggestionId(suggestionId);
    findSimilarMutation.mutate(suggestionId);
  };

  // Check if user is admin
  if (user?.role !== "admin") {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Access Denied
            </h3>
            <p className="text-muted-foreground">
              You don't have permission to access this page. Admin access is required.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Filter and sort suggestions
  let filteredSuggestions = suggestions;
  
  if (statusFilter !== "all") {
    filteredSuggestions = filteredSuggestions.filter(
      (s) => s.status === statusFilter
    );
  }

  filteredSuggestions = [...filteredSuggestions].sort((a, b) => {
    if (sortBy === "newest") {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    } else if (sortBy === "oldest") {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    } else if (sortBy === "status") {
      const statusOrder = { unread: 0, completed: 1, deferred: 2 };
      return statusOrder[a.status as keyof typeof statusOrder] - statusOrder[b.status as keyof typeof statusOrder];
    }
    return 0;
  });

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "unread":
        return "default";
      case "completed":
        return "secondary";
      case "deferred":
        return "outline";
      default:
        return "default";
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground" data-testid="heading-admin-suggestions">
            Manage Suggestions
          </h1>
        </div>
        <p className="text-muted-foreground">
          Review and manage feature requests from users
        </p>
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-wrap gap-4 mb-6 items-center justify-between">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground">Status:</label>
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as StatusFilter)}>
              <SelectTrigger className="w-[180px]" data-testid="select-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suggestions</SelectItem>
                <SelectItem value="unread">Unread</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="deferred">Deferred</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground">Sort by:</label>
            <Select value={sortBy} onValueChange={(value) => setSortBy(value as SortOption)}>
              <SelectTrigger className="w-[180px]" data-testid="select-sort-by">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
                <SelectItem value="status">Status</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button
          onClick={() => autoGroupMutation.mutate()}
          disabled={autoGroupMutation.isPending || suggestions.length === 0}
          variant="outline"
          data-testid="button-auto-group"
        >
          {autoGroupMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Grouping...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Auto-Group Similar
            </>
          )}
        </Button>
      </div>

      {/* Suggestions List */}
      {filteredSuggestions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2" data-testid="text-no-suggestions">
              No suggestions found
            </h3>
            <p className="text-muted-foreground">
              {statusFilter === "all" 
                ? "No suggestions have been submitted yet"
                : `No ${statusFilter} suggestions found`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredSuggestions.map((suggestion) => {
            const realName = suggestion.user?.firstName && suggestion.user?.lastName
              ? `${suggestion.user.firstName} ${suggestion.user.lastName}`
              : "Unknown User";

            return (
              <Card key={suggestion.id} data-testid={`card-suggestion-${suggestion.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1" data-testid={`text-suggestion-content-${suggestion.id}`}>
                      <SuggestionContent 
                        content={suggestion.content}
                        screenshots={(suggestion.screenshots as any) || []}
                      />
                    </div>
                    <Badge variant={getStatusBadgeVariant(suggestion.status)} data-testid={`badge-status-${suggestion.id}`}>
                      {suggestion.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">User:</span>
                      <span className="font-medium text-foreground" data-testid={`text-real-name-${suggestion.id}`}>
                        {realName}
                      </span>
                    </div>
                    
                    {suggestion.userAlias && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">Public alias:</span>
                        <span className="text-foreground" data-testid={`text-alias-${suggestion.id}`}>
                          {suggestion.userAlias}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span data-testid={`text-date-${suggestion.id}`}>
                        {format(new Date(suggestion.createdAt), "MMM d, yyyy 'at' h:mm a")}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant={suggestion.status === "unread" ? "default" : "outline"}
                      onClick={() => updateStatusMutation.mutate({ id: suggestion.id, status: "unread" })}
                      disabled={updateStatusMutation.isPending}
                      data-testid={`button-status-unread-${suggestion.id}`}
                    >
                      Unread
                    </Button>
                    <Button
                      size="sm"
                      variant={suggestion.status === "completed" ? "default" : "outline"}
                      onClick={() => updateStatusMutation.mutate({ id: suggestion.id, status: "completed" })}
                      disabled={updateStatusMutation.isPending}
                      data-testid={`button-status-completed-${suggestion.id}`}
                    >
                      Completed
                    </Button>
                    <Button
                      size="sm"
                      variant={suggestion.status === "deferred" ? "default" : "outline"}
                      onClick={() => updateStatusMutation.mutate({ id: suggestion.id, status: "deferred" })}
                      disabled={updateStatusMutation.isPending}
                      data-testid={`button-status-deferred-${suggestion.id}`}
                    >
                      Deferred
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleFindSimilar(suggestion.id)}
                      disabled={findSimilarMutation.isPending && selectedSuggestionId === suggestion.id}
                      data-testid={`button-find-similar-${suggestion.id}`}
                    >
                      {findSimilarMutation.isPending && selectedSuggestionId === suggestion.id ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Finding...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3 h-3 mr-1" />
                          Find Similar
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Similar Suggestions Dialog */}
      <Dialog open={similarDialogOpen} onOpenChange={setSimilarDialogOpen}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-similar-suggestions">
          <DialogHeader>
            <DialogTitle>Similar Suggestions</DialogTitle>
            <DialogDescription>
              AI-powered similarity matching found {(similarResults ?? []).length} similar suggestion{(similarResults ?? []).length !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {(similarResults ?? []).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No similar suggestions found.
              </div>
            ) : (
              (similarResults ?? []).map((result) => {
                const suggestion = suggestions.find(s => s.id === result.id);
                const realName = suggestion?.user?.firstName && suggestion?.user?.lastName
                  ? `${suggestion.user.firstName} ${suggestion.user.lastName}`
                  : "Unknown User";

                return (
                  <Card key={result.id} data-testid={`card-similar-${result.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-4">
                        <p className="text-foreground whitespace-pre-wrap flex-1" data-testid={`text-similar-content-${result.id}`}>
                          {result.content}
                        </p>
                        <Badge variant="secondary" data-testid={`badge-similarity-${result.id}`}>
                          {Math.round(result.similarity * 100)}% match
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">User:</span>
                          <span className="font-medium text-foreground">
                            {realName}
                          </span>
                        </div>
                        {suggestion && (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">Status:</span>
                              <Badge variant={getStatusBadgeVariant(suggestion.status)}>
                                {suggestion.status}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              {format(new Date(suggestion.createdAt), "MMM d, yyyy")}
                            </div>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
